export enum PublicRoutes {
  HOME = "/",
  PRIVACY_POLICY = "/privacy-policy",
  POLITICA_PRIVACIDADE = "/politica-de-privacidade",
  LGPD = "/lgpd",
  RETENCAO_DADOS = "/retencao-de-dados",
  SEGURANCA = "/seguranca",
  DISPONIBILIDADE = "/disponibilidade",
  COMO_CONTAMOS_PAGAMENTOS = "/como-a-fidexa-conta-pagamentos",
  POLITICA_CANCELAMENTO = "/politica-de-cancelamento",
  OUVIDORIA = "/ouvidoria",
  CONTATO = "/contato",
  TERMS_OF_USE = "/terms-of-use",
  TERMOS_DE_USO = "/termos-de-uso",
  AUTH = "/auth",
  AUTH_OTP = "/auth/otp",
  AUTH_OTP_CODE = "/auth/otp/code",
  AUTH_CHANGE_PASSWORD = "/auth/change-password",
  AUTH_FORGOT_PASSWORD = "/auth/forgot-password",
  AUTH_FORGOT_PASSWORD_PHONE = "/auth/forgot-password/phone",
  AUTH_FORGOT_PASSWORD_CODE = "/auth/forgot-password/code",
  AUTH_FORGOT_PASSWORD_RESET = "/auth/forgot-password/reset",
  LOGIN = "/login",
  REGISTER = "/register",
  CREDIARIO = "/crediario",
  COBRANCA_WHATSAPP = "/cobranca-whatsapp",
  REGUA_COBRANCA = "/lojistas-que-vendem-no-crediario-proprio",
  NEWSROOM = "/newsroom",
  INTEGRACOES = "/integracoes",
  FERRAMENTAS = "/ferramentas",
  FERRAMENTA_GERADOR_CARNE = "/ferramentas/gerador-de-carne",
  FERRAMENTA_SIMULADOR_CREDIARIO = "/ferramentas/simulador-de-crediario",
  FERRAMENTA_CALCULADORA_INADIMPLENCIA = "/ferramentas/calculadora-de-inadimplencia",
  HERMES = "/hermes",
  BOAS_VINDAS = "/boas-vindas",
  ALPHA7 = "/alpha7",
  INCIDENTE_2026_10_16 = "/incidentes/2026-10-16",
  DESIGN_SYSTEM = "/design-system",
  DESIGN_SYSTEM_ITEM = "/design-system/:slug",
  BLOG = "/blog",
  // Hub de cluster antes do post na declaração: é o padrão mais específico dos
  // dois e quem lê o arquivo precisa ver essa precedência sem ter de deduzi-la.
  // (Na prática não há empate — "/blog/cluster/x" tem três segmentos e
  // "/blog/:slug" só casa com dois —, mas trocar a ordem aqui e no router não
  // pode virar um 404 silencioso no dia em que o post virar "/blog/*".)
  BLOG_CLUSTER = "/blog/cluster/:slug",
  // Mesma precedência do hub: três segmentos, declarada ANTES de BLOG_POST.
  BLOG_AUTHOR = "/blog/autor/:slug",
  BLOG_POST = "/blog/:slug",
}

/** Monta a URL pública de um post a partir do slug. */
export const blogPostPath = (slug: string): string =>
  `${PublicRoutes.BLOG}/${slug}`;

/** Monta a URL pública da página de um autor a partir do slug do nome. */
export const blogAuthorPath = (slug: string): string =>
  `${PublicRoutes.BLOG}/autor/${slug}`;

/** Monta a URL pública do hub de um cluster a partir do slug. */
export const blogClusterPath = (slug: string): string =>
  `${PublicRoutes.BLOG}/cluster/${slug}`;

// FidexaHub: chat web do cliente final (magic link + OTP + rede social
// customer<->customer). Namespace de auth/sessão totalmente separado do
// PublicRoutes.AUTH* de agente — ver src/context/fidexaHubAuth.
export enum FidexaHubRoutes {
  HUB_LINK = "/hub/link/:token",
  HUB_RESUME = "/hub/resume",
  HUB_RESUME_CODE = "/hub/resume/code",
  HUB_CHAT = "/hub/chat",
  HUB_INVITE = "/hub/invite/:token",
  // Link público da loja, divulgado em rede social — não é credencial,
  // só diz com qual loja conversar depois do login.
  HUB_COMPANY_INVITE = "/hub/loja/:token",
}

/** Monta a URL do magic link/convite do FidexaHub a partir do token. */
export const hubLinkPath = (token: string): string => `/hub/link/${token}`;

// Os dois builders abaixo existem pelo mesmo motivo do de cima: montar o
// caminho à mão faz um rename de rota passar batido (sem erro de tipo, sem
// lint) e só aparecer como 404 pro cliente. O encode protege token que
// contenha "/", "?" ou "#".
export const hubInvitePath = (token: string): string =>
  `/hub/invite/${encodeURIComponent(token)}`;

export const hubCompanyInvitePath = (token: string): string =>
  `/hub/loja/${encodeURIComponent(token)}`;

export enum DashboardRoutes {
  DASHBOARD = "/dashboard",
  CUSTOMERS = "/dashboard/customers",
  REPORTS_SEND_MESSAGES = "/dashboard/reports/send-messages",
  REPORTS_UNRESPONSIVE_CLIENTS = "/dashboard/reports/unresponsive-customers",
  REPORTS_SCHEDULED_CLIENT = "/dashboard/reports/scheduled-customers",
  REPORTS_RECEIVABLES_PAID = "/dashboard/reports/receivables-paid",
  TEMPLATE_MESSAGE_RECEIVABLES = "/dashboard/template-message/receivables",
  BILLING_RANGES = "/dashboard/billing-ranges",
  MESSAGE_MANAGER = "/dashboard/message-manager",
  WHATSAPP_SETTINGS = "/dashboard/whatsapp-settings",
  SETTINGS = "/dashboard/settings",
  CHANGE_PASSWORD = "/dashboard/change-password",
  USERS = "/dashboard/users",
  CHAT = "/chat",
  LOGOUT = "logout",
  IMPORT_SHEETS = "/dashboard/import-sheets",
  IMPORT_SHEETS_ITEMS = "/dashboard/import-sheets/:importId/items",
  ROADMAP = "/dashboard/roadmap",
  TRAINING = "/dashboard/treinamento",
  TRAINING_DETAIL = "/dashboard/treinamento/:trainingId",
  TRAINING_RANKING = "/dashboard/treinamento/ranking",
  MESSAGE_SUGGESTIONS = "/dashboard/message-suggestions",
  BILLING_MANAGEMENT = "/dashboard/billing-management",
  REPORTS_CSAT = "/dashboard/reports/csat",
  FIDEXA_PAY_ACCOUNT = "/dashboard/fidexa-pay/account",
  FIDEXA_PAY_SETTINGS = "/dashboard/fidexa-pay/settings",
  FIDEXA_PAY_STATEMENTS = "/dashboard/fidexa-pay/statements",
  FIDEXA_PAY_WITHDRAW = "/dashboard/fidexa-pay/withdraw",
  /** Rota antiga do saque; redireciona para FIDEXA_PAY_WITHDRAW. */
  FIDEXA_PAY_TRANSFER_LEGACY = "/dashboard/fidexa-pay/transfer",
  CAMPAIGNS = "/dashboard/campaigns",
  CAMPAIGNS_NEW = "/dashboard/campaigns/new",
  CAMPAIGNS_SHEET_TEMPLATES = "/dashboard/campaigns/sheet-templates",
  CAMPAIGNS_RESULTS = "/dashboard/campaigns/results",
  CAMPAIGNS_RESULT_DETAIL = "/dashboard/campaigns/results/:campaignId",
  WHATSAPP_TEMPLATES = "/dashboard/whatsapp-templates",
  WHATSAPP_PROFILE = "/dashboard/whatsapp-profile",
  BILLING_RESULTS = "/dashboard/billing-results",
  BILLING_QUEUE = "/dashboard/billing-queue",
  BILLING_RESULT_FAILURES = "/dashboard/billing-results/failures",
  BILLING_RESULT_DETAIL = "/dashboard/billing-results/:rangeId",
  CREDIT_CUSTOMER = "/dashboard/crediario/consulta",
  // Outside /dashboard on purpose: it is the URL base of the WhatsApp button
  // of the Fidexa notification templates (https://fidexa.com.br/notificacoes/{{1}}).
  FIDEXA_NOTIFICATION = "/notificacoes/:id",
}

/** Monta a URL do resultado de uma campanha a partir do id. */
export const campaignResultPath = (campaignId: string): string =>
  `/dashboard/campaigns/results/${campaignId}`;

/** Billing range result URL, keeping the page query (company and period). */
export const billingResultPath = (rangeId: string, search = ""): string =>
  `/dashboard/billing-results/${rangeId}${search ? `?${search}` : ""}`;

/** Link of a Fidexa notification (the id is the notification log id). */
export const fidexaNotificationPath = (logId: string): string =>
  `/notificacoes/${encodeURIComponent(logId)}`;

/**
 * Ajustes > Agendamento: template de agendamento por loja. É para onde o 422
 * "template não configurado" do agendamento manda o usuário.
 */
export const SCHEDULE_TEMPLATE_SETTINGS_PATH =
  "/dashboard/settings?tab=schedule";

/** Monta a URL do drill-down de itens de uma importação a partir do id. */
export const importSheetsItemsPath = (importId: string): string =>
  `/dashboard/import-sheets/${importId}/items`;

/** Monta a URL de detalhe de um treinamento a partir do id. */
export const trainingDetailPath = (trainingId: string): string =>
  `/dashboard/treinamento/${trainingId}`;

export enum ChatRoutes {
  CHAT = "/v2/chat",
}

/** Monta a URL do chat v2 com a conversa já aberta. */
export const chatPath = (chatId: string): string =>
  `${ChatRoutes.CHAT}?chatId=${encodeURIComponent(chatId)}`;

export const AppRoutes = {
  ...PublicRoutes,
  ...DashboardRoutes,
  ...ChatRoutes,
  ...FidexaHubRoutes,
} as const;

export type AppRoute =
  | PublicRoutes
  | DashboardRoutes
  | ChatRoutes
  | FidexaHubRoutes;
